import type { BotDescriptor, BotDescriptors } from '@/types';
import { createBotSubStore } from './ftbot';

// Keep the existing storage keys so saved TRX connections survive the cleanup.
const AUTH_SELECTED_BOT = 'ftSelectedBot';
export type BotSubStore = ReturnType<typeof createBotSubStore>;

export const useBotStore = defineStore('ftbot-wrapper', () => {
  const selectedBot = ref('');
  const availableBots = ref<BotDescriptors>({});
  const botStores = ref<Record<string, BotSubStore>>({});
  const hasBots = computed(() => Boolean(botStores.value[selectedBot.value]));
  const activeBot = computed(() => botStores.value[selectedBot.value]);
  const selectedBotObj = computed(() => availableBots.value[selectedBot.value]);
  const nextBotId = computed(() => {
    let id = 0;
    while (`ftbot.${id}` in loggedInBots.value || `ftbot.${id}` in availableBots.value) id++;
    return `ftbot.${id}`;
  });

  function selectBot(botId: string) {
    if (!availableBots.value[botId]) return;
    localStorage.setItem(AUTH_SELECTED_BOT, botId);
    selectedBot.value = botId;
  }

  function addBot(bot: BotDescriptor) {
    if (botStores.value[bot.botId]) return;
    const connection = createBotSubStore(bot.botId);
    botStores.value[bot.botId] = connection;
    availableBots.value[bot.botId] = bot;
    void connection.fetchPing().catch(() => {});
  }

  function updateBot(botId: string, values: Partial<BotDescriptor>) {
    const store = botStores.value[botId];
    const descriptor = availableBots.value[botId];
    if (!store || !descriptor) return;
    store.updateBot(values);
    Object.assign(descriptor, values);
  }

  function removeBot(botId: string) {
    const store = botStores.value[botId];
    if (!store) return;
    store.logout();
    store.$dispose();
    delete botStores.value[botId];
    delete availableBots.value[botId];
    if (selectedBot.value === botId) {
      selectedBot.value = '';
      localStorage.removeItem(AUTH_SELECTED_BOT);
    }
  }

  return {
    selectedBot,
    availableBots,
    botStores,
    hasBots,
    activeBot,
    selectedBotObj,
    nextBotId,
    selectBot,
    addBot,
    updateBot,
    removeBot,
  };
});

export function initBots() {
  const store = useBotStore();
  if (store.hasBots) return;
  const savedId = localStorage.getItem(AUTH_SELECTED_BOT);
  const connection =
    (savedId && loggedInBots.value[savedId]) || Object.values(loggedInBots.value)[0];
  if (!connection) return;
  store.addBot(connection);
  store.selectBot(connection.botId);
}

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBotStore, import.meta.hot));
}
